import React, { useRef, useState } from 'react';
import { Loader2, Upload, Trash2, Link as LinkIcon, Scissors, Undo2 } from 'lucide-react';
import { readAndCompressImage, removeBackgroundFromDataUrl } from '../lib/image';
import { toast } from '../hooks/use-toast';
import { Input } from './ui/input';

const ImageUploader = ({
  value = '',
  onChange,
  label = 'Imagen',
  hint = 'JPG · PNG · WEBP · Máx 5 MB',
  maxWidth = 1600,
  quality = 0.85,
  format = 'auto',
  aspect = 'square', // square | wide
  allowBackgroundRemoval = true,
  testId = 'image-uploader',
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [original, setOriginal] = useState(''); // to allow "undo"
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Solo se permiten imágenes' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Imagen muy grande', description: 'Máximo 5 MB' }); return; }
    setUploading(true);
    try {
      const dataUrl = await readAndCompressImage(file, { maxW: maxWidth, quality, format });
      onChange(dataUrl);
      setOriginal('');
      toast({ title: `${label} cargada` });
    } catch {
      toast({ title: 'Error al procesar imagen' });
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const removeBg = async () => {
    if (!value) return;
    setProcessing(true);
    try {
      const cleaned = await removeBackgroundFromDataUrl(value);
      if (cleaned === value) {
        toast({ title: 'Fondo no detectado', description: 'La imagen no tiene un fondo uniforme.' });
      } else {
        setOriginal(value);
        onChange(cleaned);
        toast({ title: 'Fondo removido', description: 'Ahora se ve transparente.' });
      }
    } catch {
      toast({ title: 'Error', description: 'No se pudo remover el fondo.' });
    } finally { setProcessing(false); }
  };

  const undoBg = () => {
    if (!original) return;
    onChange(original);
    setOriginal('');
  };

  const boxHeight = aspect === 'wide' ? 'min-h-[140px]' : 'min-h-[160px]';
  // Checkerboard pattern reveals transparency clearly
  const checker = 'bg-[conic-gradient(from_0deg,rgba(255,255,255,0.06)_0.25turn,transparent_0turn_0.5turn,rgba(255,255,255,0.06)_0turn_0.75turn,transparent_0turn)] bg-[length:16px_16px]';

  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-bold text-white/60 mb-1.5 block">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid={`${testId}-dropzone`}
        className={`relative cursor-pointer rounded-xl overflow-hidden border-2 border-dashed transition-all ${boxHeight} ${
          dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-white/15 bg-white/5 hover:border-orange-500/60 hover:bg-white/[0.07]'
        }`}
      >
        {value ? (
          <>
            <div className={`w-full h-full flex items-center justify-center ${checker} p-3`}>
              <img src={value} alt="preview" className="max-w-full max-h-32 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-black text-white bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">Cargada</span>
              <div className="flex gap-1.5 flex-wrap">
                {allowBackgroundRemoval && !original && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeBg(); }}
                    disabled={processing}
                    data-testid={`${testId}-remove-bg`}
                    className="text-[10px] uppercase tracking-wider font-black text-black bg-orange-400 hover:bg-orange-500 px-2.5 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-60"
                  >
                    {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />} Quitar fondo
                  </button>
                )}
                {original && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); undoBg(); }}
                    className="text-[10px] uppercase tracking-wider font-black text-white bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                  >
                    <Undo2 className="w-3 h-3" /> Restaurar
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                  className="text-[10px] uppercase tracking-wider font-black text-black bg-white hover:bg-orange-500 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                >
                  <Upload className="w-3 h-3" /> Reemplazar
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChange(''); setOriginal(''); }}
                  className="text-[10px] uppercase tracking-wider font-black text-white bg-rose-500 hover:bg-rose-600 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Quitar
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="h-full flex flex-col items-center justify-center py-6 px-4 text-center">
            {uploading ? (
              <>
                <Loader2 className="w-7 h-7 text-orange-400 animate-spin" />
                <p className="text-sm text-white/70 mt-2">Procesando...</p>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
                  <Upload className="w-4 h-4 text-orange-400" />
                </div>
                <p className="text-white text-sm font-semibold mt-2">Arrastra o haz clic para elegir</p>
                <p className="text-white/40 text-[10px] mt-1 uppercase tracking-wider font-bold">{hint}</p>
              </>
            )}
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => setShowUrl((v) => !v)}
        className="mt-2 text-[10px] text-white/50 hover:text-white inline-flex items-center gap-1.5 uppercase tracking-wider font-bold"
      >
        <LinkIcon className="w-3 h-3" /> {showUrl ? 'Ocultar URL' : 'O usar URL externa'}
      </button>
      {showUrl && (
        <Input
          value={value?.startsWith('data:') ? '' : (value || '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://ejemplo.com/imagen.png"
          className="bg-white/5 border-white/10 text-white mt-1"
        />
      )}
    </div>
  );
};

export default ImageUploader;
